import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';

export function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      {/* Content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12 pt-24">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-10 text-center">
            About AsiliGuide
          </h1>

          {/* Mission */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Our Mission
            </h2>

            <p className="text-gray-700 leading-relaxed whitespace-pre-line">

              AsiliGuide exists to transform the operational backbone of healthcare staffing.
              Our purpose is simple and structural: streamline onboarding, automate compliance,
              empower agencies to focus on what they were built for, and delivering excellent patient care.
              <br /><br />

              In a market defined by regulatory tension, staffing shortages, and operational chaos,
              AsiliGuide provides the one thing every agency needs but rarely has: system-level clarity.
              <br /><br />

              We do this by:
              <br />
              • replacing manual onboarding with fast, digital workflows
              <br />
              • eliminating compliance uncertainty through automated tracking
              <br />
              • reducing administrative burden so staff can operate at full capacity
              <br />
              • restoring operational control in environments defined by unpredictability
              <br />
              • ensuring every caregiver is verified, prepared, and cleared without delay
              <br /><br />

              Our mission is not merely operational, it is foundational.
              We build the digital infrastructure that allows healthcare agencies to
              serve patients with confidence, free from the bottlenecks, risks,
              and administrative drag that limit care quality.
              <br /><br />
              AsiliGuide - Guided by Care.

            </p>
          </section>

          {/* Why AsiliGuide */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Why AsiliGuide?</h2>
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 sm:p-8 transition hover:shadow-md">
              <ul className="space-y-5">
                {[
                  {
                    title: 'Reduce Administrative Burden',
                    text: 'Automate repetitive tasks and eliminate manual paperwork.',
                  },
                  {
                    title: 'Ensure Compliance',
                    text: 'Stay up-to-date with healthcare regulations effortlessly.',
                  },
                  {
                    title: 'Improve Efficiency',
                    text: 'Onboard staff 70% faster than traditional methods.',
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <svg
                      className="w-6 h-6 text-[#009688] mr-3 mt-1 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div>
                      <strong className="text-gray-900">{item.title}:</strong>{' '}
                      <span className="text-gray-700">{item.text}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Our Values */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Security First',
                  text: 'Your data is protected with enterprise-grade security and HIPAA compliance.',
                },
                {
                  title: 'User-Centric',
                  text: 'Designed by healthcare professionals for healthcare professionals.',
                },
                {
                  title: 'Continuous Innovation',
                  text: 'We evolve continuously to meet the changing needs of healthcare agencies.',
                },
              ].map((value, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 transition hover:shadow-md"
                >
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-700">{value.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* The Company */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">The Company</h2>
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 sm:p-8 space-y-4">
              <p className="text-gray-700 leading-relaxed">
                AsiliGuide is a product of{' '}
                <strong className="text-gray-900">Asili LLC</strong>,
                a Virginia-registered company building digital infrastructure for the healthcare industry.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Alongside AsiliGuide, we also build{' '}
                <a
                  href="https://asiliflow.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#009688] hover:underline font-medium"
                >
                  AsiliFlow
                </a>
                , a healthcare operations platform designed to simplify scheduling,
                shift tracking, authorization management, and compliance reporting for home health agencies.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The name <em>Asili</em> comes from Swahili, meaning <em>&ldquo;origin&rdquo;</em> or{' '}
                <em>&ldquo;foundation.&rdquo;</em> It reflects our commitment to building the operational
                foundation that healthcare agencies need to deliver excellent care.
              </p>
            </div>
          </section>

          {/* CTA */}
          <section className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Ready to Get Started?</h2>
            <p className="text-gray-700 mb-6">
              Join healthcare agencies nationwide already streamlining operations with AsiliGuide.
            </p>
            <Link
              to="/preview"
              className="inline-flex items-center justify-center bg-[#009688] text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009688]"
            >
              Preview Your Checklist
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
